import React, { ReactElement, ReactNode } from "react";
import {
    User, UserGroup,
    useGetAgentsQuery,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import FilterPanel from "../../filters/FilterPanel";
import { DropDirection, FilterPropGroupItem, Filters } from "../../filters/Filters";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";
import useCurrentSession from "../../../contexts/session";

export type AgentOrGroup = {
    type: "user",
    data: User
} | {
    type: "group",
    data: UserGroup
}

export function isAgentOrGroup(item: AgentOrGroup | User | UserGroup): item is AgentOrGroup {
    return Object.hasOwn(item, "type");
}

export function isAgentOrGroupArray(item: AgentOrGroup[] | User[] | UserGroup[]): item is AgentOrGroup[] {
    return Object.hasOwn(item.firstElement, "type");
}

export interface AgentPickerProps<TIsMulti = false, TIsClearable = false, TShouldIncludeGroups = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    shouldIncludeGroups?: TShouldIncludeGroups;
    isClearable?: TIsClearable;
    exclude?: string[];
    toggleText?: string | ReactElement;
    togglePostfix?: string | ReactElement;
    shouldShowDropdown?: boolean;
    shouldShowMenu?: boolean;
    searchMinimum?: number;
    toggleClassName?: string;
    icon?: ReactNode;
    searchMaximum?: number;
    shouldShowSelfFirst?: boolean;
    drop?: DropDirection;
    shouldPortal?: boolean;

    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        agents: (TShouldIncludeGroups extends true
            ? TIsMulti extends true ? AgentOrGroup[] : AgentOrGroup
            : TIsMulti extends true ? User[] : User)
        | (TIsClearable extends true ? undefined : never)
    ) => void;
}

const AgentPicker = React.forwardRef(
    <TIsMulti extends boolean = false, TIsClearable extends boolean = false, TShouldIncludeGroups extends boolean = false>(
        {
            isMulti, onSelected, placeholder,
            minRequired, value, className, shouldIncludeGroups, isClearable, exclude,
            shouldShowDropdown, searchMinimum, toggleClassName, toggleText, shouldShowMenu,
            icon, searchMaximum, shouldShowSelfFirst, drop, shouldPortal, togglePostfix,
        }: AgentPickerProps<TIsMulti, TIsClearable, TShouldIncludeGroups>, ref: React.ForwardedRef<SelectInstanceHandler>,
    ): ReactElement => {
        const { data, loading, error } = useGetAgentsQuery({
            variables: {
                shouldIncludeGroups: shouldIncludeGroups as boolean,
            },
        });

        const session = useCurrentSession();

    type SelectedType = TIsMulti extends true ? readonly Option<AgentOrGroup>[] : Option<AgentOrGroup>;
    type ValueType = TIsMulti extends true ? Option<AgentOrGroup>[] : Option<AgentOrGroup>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<AgentOrGroup>).value) as Parameters<typeof onSelected>[0];
        const agents = (Array.isArray(selected)
            ? selected.map(row => (row as Option<AgentOrGroup>).data)
            : (selected as Option<AgentOrGroup>).data) as Parameters<typeof onSelected>[1];
        onSelected(values, agents);
    };

    const excludeSet = new Set(exclude ?? []);
    let agents = data?.settings?.agents?.filter(row => !excludeSet.has(row.id));

    if (shouldShowSelfFirst && session) {
        const current = agents?.find(row => row.isCurrentlyLoggedInUser);
        agents = [current, ...agents?.filter(row => !row.isCurrentlyLoggedInUser) ?? []].filterNull();
    }

    const agentValues: Option<Partial<AgentOrGroup>>[] = agents?.map(agent => ({
        label: (
            <>
                <Avatar src={agent.avatarUrl as string} variant="xs" />
                {agent.name}
            </>
        ),
        value: agent.id.toString() as string,
        text: agent.name,
        data: {
            type: "user",
            data: agent as User,
        },
    })) ?? [];

    const groupValues: Option<Partial<AgentOrGroup>>[] = shouldIncludeGroups
        ? data?.userGroups?.filter(row => !excludeSet.has(row.id)).map(group => ({
            label: group.name as string,
            value: group.id.toString() as string,
            data: {
                type: "group",
                data: group as UserGroup,
            },
        })) ?? []
        : [];

    let options: (Option<Partial<AgentOrGroup>> | OptionWithChildren<Partial<AgentOrGroup>>)[];

    options = agentValues;

    if (shouldIncludeGroups) {
        options = [
            {
                label: "Groups",
                options: groupValues,
            },
            {
                label: "Agents",
                options: agentValues,
            },
        ];
    }

    const values = [...agentValues, ...groupValues];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                togglePostfix={togglePostfix}
                isMulti={isMulti}
                searchMinimum={searchMinimum}
                categories={options as FilterPropGroupItem<AgentOrGroup>[]}
                className={className}
                icon={icon}
                searchMaximum={searchMaximum}
                toggleClassName={toggleClassName}
                drop={drop}
                shouldPortal={shouldPortal}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => {
                    if (isMulti) {
                        const handler = (onSelected as ((value: string[], users: (AgentOrGroup | User)[]) => void));
                        const list = Array.from(current);
                        handler(list, list.map(id => (shouldIncludeGroups
                            ? valueMap[id].data as AgentOrGroup
                            : valueMap[id].data?.data as User)));
                    } else {
                        const handler = (onSelected as ((value: string, user: AgentOrGroup | User) => void));
                        handler(current as string, shouldIncludeGroups
                            ? valueMap[current as string].data as AgentOrGroup
                            : valueMap[current as string].data?.data as User);
                    }
                }}
            />
        );
    }
    if (shouldShowMenu) {
        return (
            <FilterPanel
                isMulti={isMulti}
                searchMinimum={searchMinimum}
                categories={options as FilterPropGroupItem<AgentOrGroup>[]}
                className={className}
                searchMaximum={searchMaximum}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => {
                    if (isMulti) {
                        const handler = (onSelected as ((value: string[], users: (AgentOrGroup | User)[]) => void));
                        const list = Array.from(current);
                        handler(list, list.map(id => (shouldIncludeGroups
                            ? valueMap[id].data as AgentOrGroup
                            : valueMap[id].data?.data as User)));
                    } else {
                        const handler = (onSelected as ((value: string, user: AgentOrGroup | User) => void));
                        handler(current as string, shouldIncludeGroups
                            ? valueMap[current as string].data as AgentOrGroup
                            : valueMap[current as string].data?.data as User);
                    }
                }}
            />
        );
    }
    return (
        <Select
            ref={ref}
            filterOption={(member, input) => {
                if (input) {
                    const lower = input.toLowerCase();
                    return (member.data as UserGroup).name?.toLowerCase().includes(lower)
                     || (member.data as User).name?.toLowerCase().includes(lower);
                }

                return true;
            }}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={(options ?? []) as Option<AgentOrGroup>[]}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}
        />
    );
    },
);

AgentPicker.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    shouldIncludeGroups: false,
    isClearable: undefined,
    exclude: [],
    toggleText: "",
    togglePostfix: undefined,
    searchMinimum: undefined,
    shouldShowDropdown: false,
    icon: undefined,
    toggleClassName: "",
    shouldShowMenu: false,
    searchMaximum: undefined,
    shouldShowSelfFirst: false,
    drop: undefined,
    shouldPortal: false,
};

export default AgentPicker as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
    TShouldIncludeGroups extends boolean = false,
> (
    props: AgentPickerProps<TMulti, TIsClearable, TShouldIncludeGroups> & { ref?: React.ForwardedRef<SelectInstanceHandler>; }
) => ReturnType<typeof AgentPicker>;
